export const useGoogleRecenter = async (
  map: Ref<google.maps.Map | undefined>,
  initialCenterOnLoad: Ref<boolean>,
  zoom: Ref<number | undefined>,
  clusterCutoffLimit: Ref<number>,
  assets: Ref<Array<MapAsset> | undefined>,
  clusters: Ref<Array<GeoCluster> | undefined>,
  picker: Ref<boolean | undefined>,
  pickerCoordinates: Ref<LatLngAlt | undefined>,
  forceInteractionFunction: () => unknown,
  afterClusterUpdate: () => unknown,
  // A function that goes to the last view of the map upon initialization, returns true if it was successful
  goToLastView: () => boolean,
) => {
  const { bounds, resetBounds, updateBounds } = useFeatureBounds()

  const { $googleApiPromise } = useNuxtApp()
  const loadedGoogle = (await $googleApiPromise) as typeof google

  const initialized = ref(false)
  const recenterAfterAssetLoad = ref(false)

  const recenter = () => {
    if (!map.value) return

    const googleBounds = getBounds()

    if (zoom.value) {
      const center = googleBounds.getCenter()

      map.value.setZoom(zoom.value)
      map.value.setCenter(center)
      return
    }

    const northEast = googleBounds.getNorthEast()
    const southWest = googleBounds.getSouthWest()
    const isSinglePoint = northEast.lat() === southWest.lat() && northEast.lng() === southWest.lng()
    const convexHull = clusters.value?.[0]?.convexHull

    if (!isSinglePoint || !convexHull) {
      map.value.fitBounds(googleBounds)
      return
    }

    const polygonBounds = new loadedGoogle.maps.LatLngBounds()
    convexHull.forEach(([lng, lat]) => {
      polygonBounds.extend(new loadedGoogle.maps.LatLng(lat, lng))
    })

    map.value.fitBounds(polygonBounds)
  }

  const getBounds = (): google.maps.LatLngBounds => {
    const centerOfNetherlands = new loadedGoogle.maps.LatLng(52.1009263, 5.6462682)
    const defaultBounds = new loadedGoogle.maps.LatLngBounds(centerOfNetherlands)

    if (bounds.value.east === Infinity) return defaultBounds
    if (bounds.value.west === Infinity) return defaultBounds
    if (bounds.value.north === Infinity) return defaultBounds
    if (bounds.value.south === Infinity) return defaultBounds

    return new loadedGoogle.maps.LatLngBounds(bounds.value)
  }

  const updateClusterBounds = () => {
    if (!clusters.value || !map.value) {
      return
    }

    resetBounds(["clusters"])
    updateAssetBounds()

    clusters.value.forEach((cluster) => {
      updateBounds("clusters", cluster.center[1], cluster.center[0])
    })

    if (!initialized.value) {
      const initializedComplete = initialize()

      if (initializedComplete) {
        return
      }
    }

    if (!initialCenterOnLoad.value) {
      recenter()
    }

    if ((map.value?.getZoom() ?? 0) >= clusterCutoffLimit.value) {
      forceInteractionFunction()
      if (!initialCenterOnLoad.value) {
        recenterAfterAssetLoad.value = true
      }
    }

    afterClusterUpdate()
    initialCenterOnLoad.value = true
  }

  const initialize = () => {
    initialized.value = true

    const succesFullyWentToLastView = goToLastView()

    if (succesFullyWentToLastView) {
      if ((map.value?.getZoom() ?? 0) >= clusterCutoffLimit.value) {
        forceInteractionFunction()
      }

      return true
    }

    return false
  }

  const updateAssetBounds = () => {
    resetBounds(["markers"])

    if (!assets.value || !assets.value.length) {
      return
    }

    assets.value.forEach((asset) => {
      updateBounds("markers", asset.location.gps!.coordinates[1], asset.location.gps!.coordinates[0])
    })

    if (recenterAfterAssetLoad.value) {
      recenter()
      recenterAfterAssetLoad.value = false
    }
  }

  const updatePicker = () => {
    resetBounds(["picker"])

    if (!picker.value || !pickerCoordinates.value) return

    updateBounds("picker", pickerCoordinates.value.lat, pickerCoordinates.value.lng)
  }

  watch(clusters, () => updateClusterBounds())
  watch(assets, () => updateAssetBounds())
  watch([picker, pickerCoordinates], () => updatePicker(), { immediate: true })

  return {
    recenter,
    updateClusterBounds,
  }
}
